import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import storefrontService from 'services/storefrontService';
import { AuthPageConfig, FeatureFlags, NavigationConfig, PageLayout, ThemeConfig } from 'types/storefront';
import { PaletteMode } from '@mui/material';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { tenantStorage } from 'utils/tenantStorage';
import { defaultAuthPageConfig } from 'utils/authPage';
import { setConfiguredProductPlaceholder } from 'utils/productImage';
import { readCachedTheme, writeCachedTheme } from 'utils/themeCache';

interface StorefrontContextValue {
  theme: ThemeConfig | null;
  navigation: NavigationConfig;
  features: FeatureFlags;
  authPage: AuthPageConfig;
  colorMode: PaletteMode;
  setColorMode: (mode: PaletteMode) => void;
  getLayout: (page: string) => Promise<PageLayout>;
  reloadFeatures: () => Promise<void>;
  loading: boolean;
}

const defaultNavigation: NavigationConfig = { header: [], footer: [] };
const defaultFeatures: FeatureFlags = {
  promotionsEnabled: true,
  brandsEnabled: true,
  hidePricesForAnonymous: false,
  multipleCartsEnabled: false,
  b2bPortalEnabled: true,
  storePaused: false,
};

const StorefrontContext = createContext<StorefrontContextValue | undefined>(undefined);

export const StorefrontProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeConfig | null>(readCachedTheme);
  const [navigation, setNavigation] = useState<NavigationConfig>(defaultNavigation);
  const [features, setFeatures] = useState<FeatureFlags>(defaultFeatures);
  const [authPage, setAuthPage] = useState<AuthPageConfig>(() => defaultAuthPageConfig());
  const [loading, setLoading] = useState(true);
  const [colorMode, setColorModeState] = useState<PaletteMode>(
    () => (tenantStorage.getItem(STORAGE_KEYS.COLOR_MODE) as PaletteMode) || 'light'
  );

  useEffect(() => {
    const hadCachedTheme = readCachedTheme() != null;
    const load = async () => {
      setLoading(true);
      try {
        const [themeData, navData, featuresData, authData] = await Promise.all([
          storefrontService.getTheme(),
          storefrontService.getNavigation(),
          storefrontService.getFeatures(),
          storefrontService.getAuthPage(),
        ]);
        if (themeData) {
          setTheme(themeData);
          writeCachedTheme(themeData);
          setConfiguredProductPlaceholder(themeData.placeholderImageUrl);
          const persistedColorMode = tenantStorage.getItem(STORAGE_KEYS.COLOR_MODE);
          if (!persistedColorMode && themeData.defaultColorMode) {
            setColorModeState(themeData.defaultColorMode);
          }
        } else if (!hadCachedTheme) {
          setConfiguredProductPlaceholder(null);
        }
        setNavigation(navData);
        setFeatures({ ...defaultFeatures, ...featuresData });
        setAuthPage(authData);
      } catch (error) {
        console.error('Error loading storefront config:', error);
        if (!hadCachedTheme) setConfiguredProductPlaceholder(null);
        setNavigation(defaultNavigation);
        setFeatures(defaultFeatures);
        setAuthPage(defaultAuthPageConfig());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const setColorMode = (mode: PaletteMode) => {
    tenantStorage.setItem(STORAGE_KEYS.COLOR_MODE, mode);
    setColorModeState(mode);
  };

  const getLayout = useCallback((page: string) => storefrontService.getLayout(page), []);

  const reloadFeatures = useCallback(async () => {
    try {
      const featuresData = await storefrontService.getFeatures();
      setFeatures(featuresData);
    } catch {
      /* se mantiene la configuración ya cargada */
    }
  }, []);

  useEffect(() => {
    const onFocus = () => {
      void reloadFeatures();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [reloadFeatures]);

  const value = useMemo(
    () => ({ theme, navigation, features, authPage, colorMode, setColorMode, getLayout, reloadFeatures, loading }),
    [theme, navigation, features, authPage, colorMode, getLayout, reloadFeatures, loading]
  );

  return <StorefrontContext.Provider value={value}>{children}</StorefrontContext.Provider>;
};

export const useStorefrontContext = () => {
  const ctx = useContext(StorefrontContext);
  if (!ctx) throw new Error('useStorefrontContext debe usarse dentro de StorefrontProvider');
  return ctx;
};
